import React, { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { Plus, Bell } from 'lucide-react';
import Modal from '../components/ui/Modal';
import DataGrid from '../components/grid/DataGrid';
import { useQuery as useRQ } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input } from '../components/ui/input';

const notificationSchema = yup.object({
  title: yup.string().required('Title is required').max(100, 'Title must be less than 100 characters'),
  message: yup.string().required('Message is required').max(1000, 'Message must be less than 1000 characters'),
  notificationType: yup.string().required(),
  priority: yup.string().required(),
  isActive: yup.boolean().required(),
});

type NotificationFormData = yup.InferType<typeof notificationSchema>;

const AdminNotificationsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['adminNotifications', page, pageSize],
    queryFn: () => apiService.getAdminNotifications(page, pageSize)
  });

  const [showCreate, setShowCreate] = useState(false);
  const [notificationType, setNotificationType] = useState('Announcement');
  const [priority, setPriority] = useState('Normal');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recipientMethod, setRecipientMethod] = useState<'all' | 'specific'>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<NotificationFormData>({
    resolver: yupResolver(notificationSchema),
    defaultValues: {
      title: '',
      message: '',
      notificationType: 'Announcement',
      priority: 'Normal',
      isActive: true
    }
  });

  // Load users for "Specific Users" selection
  const { data: companyUsersResp } = useRQ({
    queryKey: ['company-users-for-notifications'],
    queryFn: () => apiService.getCompanyUsers(),
  });
  const allUsers = (companyUsersResp?.data || []) as any[];
  const [userSearch, setUserSearch] = useState('');
  const users = allUsers.filter(u => (
    (u.firstName + ' ' + u.lastName + ' ' + (u.email||'')).toLowerCase().includes(userSearch.toLowerCase())
  ));

  const handleCreate = async (data: NotificationFormData) => {
    setSaving(true);
    try {
      const payload: any = {
        title: data.title,
        message: data.message,
        notificationType: data.notificationType,
        priority: data.priority,
        isActive: data.isActive,
        recipientType: recipientMethod === 'all' ? 'All' : 'SpecificUsers',
        recipientConfig: recipientMethod === 'all' ? null : JSON.stringify({ userIds: selectedUserIds })
      };
      await apiService.createAdminNotification(payload);
      setShowCreate(false);
      reset();
      setNotificationType('Announcement');
      setPriority('Normal');
      setIsActive(true);
      setRecipientMethod('all');
      setSelectedUserIds([]);
      refetch();
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [page, pageSize, refetch]);

  const notifications = data?.data?.data || [];
  const totalPages = data?.data?.totalPages || 1;

  // Search functionality
  const handleSearch = (term: string) => {
    setSearch(term);
  };

  const filteredNotifications = useMemo(() => {
    if (!search) return notifications;
    return notifications.filter((notification: any) => 
      notification.title.toLowerCase().includes(search.toLowerCase()) ||
      notification.notificationType.toLowerCase().includes(search.toLowerCase()) ||
      notification.priority.toLowerCase().includes(search.toLowerCase())
    );
  }, [notifications, search]);

  // Define columns for DataGrid
  const columns = useMemo(() => [
    {
      id: 'title',
      header: 'Title',
      sortable: true,
      cell: (value: string) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{value}</div>
      )
    },
    {
      id: 'notificationType',
      header: 'Type',
      sortable: true,
      cell: (value: string) => (
        <div className="text-sm text-gray-700 dark:text-gray-300">{value}</div>
      )
    },
    {
      id: 'priority',
      header: 'Priority',
      sortable: true,
      cell: (value: string) => (
        <div className="text-sm text-gray-700 dark:text-gray-300">{value}</div>
      )
    },
    {
      id: 'isActive',
      header: 'Active',
      sortable: true,
      cell: (value: boolean) => (
        <div className="text-sm text-gray-700 dark:text-gray-300">{value ? 'Yes' : 'No'}</div>
      )
    },
    {
      id: 'createdDate',
      header: 'Created',
      sortable: true,
      cell: (value: string) => (
        <div className="text-sm text-gray-500 dark:text-gray-400">{new Date(value).toLocaleString()}</div>
      )
    }
  ], []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bell className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Notifications</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage system notifications and announcements</p>
          </div>
        </div>
        <button onClick={()=>setShowCreate(true)} className="btn-primary inline-flex items-center">
          <Plus className="h-4 w-4 mr-2" /> New Notification
        </button>
      </div>

      <DataGrid
        data={filteredNotifications}
        columns={columns}
        isLoading={isLoading}
        searchTerm={search}
        onSearch={handleSearch}
        currentPage={page}
        pageSize={pageSize}
        totalCount={notifications.length}
        totalPages={totalPages}
        onPageChange={setPage}
        emptyMessage="No notifications found"
        searchPlaceholder="Search notifications by title, type, or priority..."
      />

      {/* Create Notification Modal */}
      <Modal isOpen={showCreate} onClose={()=>{
        setShowCreate(false);
        reset();
      }} title="Create Notification" maxWidth="xl">
        <form onSubmit={handleSubmit(handleCreate)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  maxLength={100}
                  placeholder="Enter notification title"
                  className={errors.title ? 'border-red-500' : ''}
                />
              )}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  maxLength={1000}
                  rows={6}
                  placeholder="Enter notification message"
                  className={`w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
              )}
            />
            {errors.message && (
              <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <Controller
                name="notificationType"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    onChange={(e) => {
                      field.onChange(e);
                      setNotificationType(e.target.value);
                    }}
                  >
                    {['Announcement','Alert','Maintenance','System','Emergency'].map(t=> (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                )}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    onChange={(e) => {
                      field.onChange(e);
                      setPriority(e.target.value);
                    }}
                  >
                    {['Low','Normal','High','Urgent'].map(p=> (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                )}
              />
            </div>
            <div className="flex items-center space-x-2 mt-6">
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <input
                    id="active"
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      setIsActive(e.target.checked);
                    }}
                  />
                )}
              />
              <label htmlFor="active" className="text-sm text-gray-700 dark:text-gray-300">Active</label>
            </div>
          </div>
          {/* Recipient selection (MVP) */}
          <div className="pt-2 border-t">
            <div className="text-sm font-medium text-gray-700 mb-2">Recipients</div>
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input type="radio" name="recipient" checked={recipientMethod==='all'} onChange={()=>setRecipientMethod('all')} />
                <span className="text-sm text-gray-700">All Users (company-wide)</span>
              </label>
              <div>
                <label className="flex items-center space-x-2">
                  <input type="radio" name="recipient" checked={recipientMethod==='specific'} onChange={()=>setRecipientMethod('specific')} />
                  <span className="text-sm text-gray-700">Specific Users</span>
                </label>
                {recipientMethod==='specific' && (
                  <div className="mt-2">
                    {/* Selected chips */}
                    {selectedUserIds.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-2">
                        {selectedUserIds.map(id => {
                          const u = allUsers.find(x=>x.id===id);
                          if (!u) return null;
                          return (
                            <span key={id} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent-color-100 text-accent-color-800">
                              {u.firstName} {u.lastName}
                              <button type="button" className="ml-1 text-accent-color-700" onClick={()=>setSelectedUserIds(prev=>prev.filter(x=>x!==id))}>×</button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <input
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 mb-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Search users by name or email..."
                      value={userSearch}
                      onChange={(e)=>setUserSearch(e.target.value)}
                    />
                    <div className="border rounded-md max-h-48 overflow-auto">
                      {users.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500">No users match your search.</div>
                      ) : users.map((u:any)=> {
                        const checked = selectedUserIds.includes(u.id);
                        return (
                          <label key={u.id} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-50">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e)=>{
                                  setSelectedUserIds(prev => e.target.checked ? [...prev, u.id] : prev.filter(x=>x!==u.id));
                                }}
                              />
                              <div>
                                <div className="text-sm text-gray-900">{u.firstName} {u.lastName}</div>
                                <div className="text-xs text-gray-500">{u.email}</div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="pt-2 text-right">
            <button type="button" className="btn-secondary mr-2" onClick={()=>setShowCreate(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminNotificationsPage;


